import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
  FirstLoginConfiguration,
  FirstLoginUserProfile,
  SubscribeContext,
  SubscribeFormValues,
} from 'interfaces/subscribe.types';
import {
  appConfigSelector,
  featureSelector,
  isAuthenticatedSelector,
} from 'reducers/app.reducer';
import { buildCountryOptions } from '../../../constants';
import { useFirstLoginFeature } from './useFirstLoginFeature';
import { useFirstLoginUser } from './useFirstLoginUser';
import { useRegistrationFieldConfigs } from './useRegistrationFieldConfigs';

// ---------- Helpers ----------

/**
 * Détermine le contexte d'accès au formulaire en fonction
 * des données disponibles (token, données user).
 *
 * - Sans token → auto-inscription
 * - Avec token + isSso true → first-login (SSO) : le champ password est masqué
 * - Avec token + isSso false → pre-registration (email d'invitation) : le champ password est affiché
 */
function resolveContext(
  hasToken: boolean,
  firstLoginUser: FirstLoginUserProfile | undefined,
): SubscribeContext {
  if (!hasToken) return 'autonomous-registration';
  if (firstLoginUser) {
    return firstLoginUser.isSso ?? false ? 'first-login' : 'pre-registration';
  }
  return 'pre-registration';
}

/**
 * Construit les valeurs initiales du formulaire en fonction du contexte.
 */
function buildInitialValues(
  context: SubscribeContext,
  firstLoginUser?: FirstLoginUserProfile,
  defaultCountryId?: number,
): SubscribeFormValues {
  const empty: SubscribeFormValues = {
    gender: '',
    lastName: '',
    firstName: '',
    companyName: '',
    email: '',
    clientId: '',
    password: '',
    confirmPassword: '',
    optin: false,
    terms: false,
    dataPolicy: false,
    referralCode: '',
    phone: '',
    addressName: '',
    addressGender: '',
    addressLastName: '',
    addressFirstName: '',
    addressCompanyName: '',
    addressStreet: '',
    addressComplement: '',
    addressPostalCode: '',
    addressCity: '',
    addressCountry: defaultCountryId != null ? String(defaultCountryId) : '',
  };

  if (context === 'autonomous-registration' || !firstLoginUser) {
    return empty;
  }

  const defaultAddress =
    firstLoginUser.profile.addresses?.find((a) => a.isDefault) ??
    firstLoginUser.profile.addresses?.[0];

  // Pré-remplir avec les données du back
  return {
    ...empty,
    gender:
      firstLoginUser.profile.gender != null
        ? String(firstLoginUser.profile.gender)
        : '',
    lastName: firstLoginUser.profile.lastName || '',
    firstName: firstLoginUser.profile.firstName || '',
    companyName: firstLoginUser.profile.companyName || '',
    email: firstLoginUser.email || '',
    clientId: firstLoginUser.clientId || '',
    phone: firstLoginUser.profile.phone || '',
    addressName: defaultAddress?.title || '',
    addressGender:
      defaultAddress?.gender != null ? String(defaultAddress.gender) : '',
    addressLastName: defaultAddress?.lastName || '',
    addressFirstName: defaultAddress?.firstName || '',
    addressCompanyName: defaultAddress?.companyName || '',
    addressStreet: defaultAddress?.line1 || '',
    addressComplement: defaultAddress?.line2 || '',
    addressPostalCode: defaultAddress?.zipCode || '',
    addressCity: defaultAddress?.city || '',
    addressCountry:
      defaultAddress?.countryId != null
        ? String(defaultAddress.countryId)
        : empty.addressCountry,
  };
}

/**
 * Titre dynamique selon le contexte.
 */
function resolveTitle(
  context: SubscribeContext,
  t: (key: string) => string,
): string {
  switch (context) {
    case 'autonomous-registration':
      return t('auth.subscribe.title_register');
    case 'pre-registration':
      return t('auth.subscribe.title_continue_registration');
    case 'first-login':
      return t('auth.subscribe.title_confirm_info');
    default:
      return t('auth.subscribe.title_register');
  }
}

/**
 * Texte du CTA dynamique selon le contexte.
 */
function resolveSubmitLabel(
  context: SubscribeContext,
  t: (key: string) => string,
): string {
  switch (context) {
    case 'autonomous-registration':
      return t('auth.signup.submit');
    case 'pre-registration':
      return t('auth.subscribe.submit_continue');
    case 'first-login':
      return t('auth.subscribe.submit_confirm');
    default:
      return t('auth.signup.submit');
  }
}

// ---------- Main hook ----------

export interface UseSubscribeFormReturn {
  /** Contexte résolu */
  context: SubscribeContext;
  /** Valeurs initiales du formulaire */
  initialValues: SubscribeFormValues;
  /** Titre de la page */
  title: string;
  /** Texte du bouton submit */
  submitLabel: string;
  /** L'utilisateur a-t-il accès à la page ? */
  hasAccess: boolean;
  /** Token présent mais invalide/expiré → page "lien invalide" */
  isLinkInvalid: boolean;
  /** Token présent mais compte déjà activé (1ère connexion déjà complétée) → page dédiée */
  isAlreadyActive: boolean;
  /** Données en cours de chargement */
  isLoading: boolean;
  /** Email et matricule sont en lecture seule (pré-inscription / first-login) */
  isIdentityReadOnly: boolean;
  /** Feature first_login activée */
  isFirstLoginEnabled: boolean;
  /** Inscription autonome activée (postUserRegister) */
  isSubscriptionEnabled: boolean;
  /** Nom d'entreprise requis */
  isCompanyNameRequired: boolean;
  /** Feature referral activée */
  isReferralEnabled: boolean;
  /** Configuration des champs depuis le BO */
  fieldConfigs: ReturnType<typeof useRegistrationFieldConfigs>['data'];
  /** Config globale de l'app */
  appConfig: ReturnType<typeof useSelector>;
  /** Configuration de la feature first_login (submitText, validateText, redirectUrl) */
  firstLoginConfiguration?: FirstLoginConfiguration;
}

export function useSubscribeForm(): UseSubscribeFormReturn {
  const { t } = useTranslation();
  const isAuthenticated = useSelector(isAuthenticatedSelector);
  const hasToken = isAuthenticated;

  const appConfig = useSelector(appConfigSelector);
  const isAppConfigLoaded = Object.keys(appConfig).length > 0;
  const isSubscriptionEnabled = !!appConfig.postUserRegister;

  const postUserRegisterFeature = useSelector(
    featureSelector('post_user_register'),
  );
  const referralFeature = useSelector(featureSelector('referral'));
  const isCompanyNameRequired =
    !!postUserRegisterFeature?.data?.attrs?.company_name_required;
  const isReferralEnabled = !!referralFeature?.data?.enabled;

  // React Query hooks
  const {
    data: firstLoginUser,
    isLoading: isFirstLoginUserLoading,
    isError: isFirstLoginUserError,
    error: firstLoginUserError,
  } = useFirstLoginUser(hasToken);

  const {
    data: firstLoginFeature,
    isLoading: isFirstLoginFeatureLoading,
    isError: isFirstLoginFeatureError,
  } = useFirstLoginFeature();

  const { data: fieldConfigs, isLoading: isFieldConfigsLoading } =
    useRegistrationFieldConfigs();

  const isFirstLoginEnabled = !!firstLoginFeature?.enabled;
  const firstLoginConfiguration = firstLoginFeature?.configuration;

  // Résolution du contexte
  const context = useMemo(
    () => resolveContext(hasToken, firstLoginUser),
    [hasToken, firstLoginUser],
  );

  // Premier pays de livraison comme valeur par défaut
  const defaultCountryId = useMemo(() => {
    const options = buildCountryOptions(appConfig.deliveryCountriesIds);
    return options.length > 0 ? options[0].value : undefined;
  }, [appConfig.deliveryCountriesIds]);

  // Valeurs initiales
  const initialValues = useMemo(
    () => buildInitialValues(context, firstLoginUser, defaultCountryId),
    [context, firstLoginUser, defaultCountryId],
  );

  // Titre et CTA
  const title = resolveTitle(context, t);
  const defaultSubmitLabel = resolveSubmitLabel(context, t);
  // Le BO peut fournir un texte personnalisé pour le bouton submit
  const submitLabel =
    firstLoginConfiguration?.submitText?.trim() || defaultSubmitLabel;

  // Logique d'accès :
  // - Avec token → accès si le call first-login a réussi
  // - Sans token → accès si inscription autonome activée
  const hasAccess = hasToken
    ? !isFirstLoginUserError && !isFirstLoginUserLoading
    : isSubscriptionEnabled;

  // Compte déjà activé : token présent + API first-login retourne le code
  // métier dédié (1ère connexion déjà complétée, statut passé à actif)
  const isAlreadyActive =
    hasToken && firstLoginUserError?.response?.data?.code === 'account_already_active';

  // Lien déjà utilisé : token Redux présent + API first-login retourne 403
  // (Le cas lien expiré/401 est géré directement par l'intercepteur axios dans api.ts)
  const isLinkInvalid = hasToken && isFirstLoginUserError && !isAlreadyActive;

  const isLoading =
    !isAppConfigLoaded ||
    isFirstLoginUserLoading ||
    // Ne pas bloquer le form si le call first_login feature échoue
    (isFirstLoginFeatureLoading && !isFirstLoginFeatureError) ||
    isFieldConfigsLoading;

  const isIdentityReadOnly = context !== 'autonomous-registration';

  return {
    context,
    initialValues,
    title,
    submitLabel,
    hasAccess,
    isLinkInvalid,
    isAlreadyActive,
    isLoading,
    isIdentityReadOnly,
    isFirstLoginEnabled,
    isSubscriptionEnabled,
    isCompanyNameRequired,
    isReferralEnabled,
    fieldConfigs,
    appConfig,
    firstLoginConfiguration,
  };
}
